// ─────────────────────────────────────────────────────────────────────────
// Glass-i — Item Master picker for purchase line grids (Stage 6B lock).
//
// A borderless, searchable AntD Select bound to the Item Master lookup
// (active + purchasable items only). End users CANNOT type a free product
// name here — they must pick a master item. The full selected item is handed
// back via `onSelectItem(id, item)` so the calling grid can snapshot
// name / code / uom / hsn / gst / purchase-rate onto the line.
//
// Used only for PRODUCT lines. SERVICE / OTHER lines keep their free-text
// Input (those are exempt from the master lock).
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { itemsApi } from '../masters/items/itemsApi';

// Normalise the lookup result to a plain array regardless of envelope shape
// (the api may return an array OR a { rows: [...] } object).
function toRows(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.rows)) return res.rows;
  return [];
}

export default function ItemMasterSelect({
  value,
  label,
  disabled = false,
  size = 'small',
  onSelectItem,
  onClear
}) {
  // Seed the options with the already-chosen item so the label renders even
  // before the first search (edit / reload case).
  const [options, setOptions] = useState(() =>
    value && label ? [{ value, label, item: null }] : []);
  const [fetching, setFetching] = useState(false);
  const seq = useRef(0);

  const search = useCallback(async (q) => {
    const mine = ++seq.current;
    setFetching(true);
    try {
      const res = await itemsApi.lookup({
        q: q || '',
        limit: 20,
        status: 'active',
        isPurchasable: true
      });
      if (mine !== seq.current) return; // a newer search superseded this one
      setOptions(toRows(res).map((it) => ({
        value: it.id,
        label: `${it.itemCode || it.item_code || ''} — ${it.itemName || it.item_name || ''}`,
        item: it
      })));
    } catch (_e) {
      if (mine === seq.current) setOptions([]);
    } finally {
      if (mine === seq.current) setFetching(false);
    }
  }, []);

  return (
    <Select
      showSearch
      allowClear
      size={size}
      variant="borderless"
      placeholder="Pick item from master…"
      style={{ width: '100%' }}
      value={value || undefined}
      disabled={disabled}
      filterOption={false}
      onSearch={search}
      onFocus={() => { if (options.length === 0) search(''); }}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={options}
      onClear={() => onClear && onClear()}
      onChange={(val, opt) => {
        if (!val) { onClear && onClear(); return; }
        onSelectItem && onSelectItem(val, (opt && opt.item) || null);
      }}
    />
  );
}
